import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { fmtDateTimeBoth } from '../utils/dates';
import { isManager, ROLE_AR } from '../utils/roles';

const ACTION_LABELS = {
  login: 'تسجيل دخول',
  login_failed: 'محاولة دخول فاشلة',
  logout: 'تسجيل خروج',
  password_changed: 'تغيير كلمة المرور',
  user_created: 'إنشاء مستخدم',
  user_updated: 'تعديل بيانات مستخدم',
  user_password_reset: 'إعادة تعيين كلمة مرور',
  client_created: 'إضافة عميل',
  client_updated: 'تعديل عميل',
  client_impersonated: 'دخول إلى بوابة عميل',
  client_impersonation_ended: 'إنهاء معاينة بوابة عميل',
  task_created: 'إنشاء مهمة',
  attachment_uploaded: 'رفع مرفق',
  attachment_deleted: 'حذف مرفق',
  settings_updated: 'تحديث إعدادات',
  activity_log_deleted: 'حذف سجل عملية',
  activity_log_cleared: 'مسح سجلات قديمة',
};

const ACTION_BADGE = {
  login: 'completed',
  login_failed: 'cancelled',
  logout: 'pending',
  password_changed: 'in_progress',
  user_created: 'approved',
  user_updated: 'in_progress',
  user_password_reset: 'waiting_approval',
  client_created: 'approved',
  client_updated: 'in_progress',
  client_impersonated: 'waiting_approval',
  client_impersonation_ended: 'pending',
  task_created: 'approved',
  attachment_uploaded: 'in_progress',
  attachment_deleted: 'cancelled',
  settings_updated: 'waiting_approval',
  activity_log_deleted: 'cancelled',
  activity_log_cleared: 'cancelled',
};

export default function ActivityLog() {
  const { api, user } = useAuth();
  const toast = useToast();
  const canFilterByUser = isManager(user);

  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);

  const [filters, setFilters] = useState({ user_id: '', action: '', from: '', to: '', search: '' });

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, per_page: 50 });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await api(`/activity-logs?${params.toString()}`);
      setLogs(res.data || []);
      setPage(res.current_page || 1);
      setLastPage(res.last_page || 1);
      setTotal(res.total || 0);
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  }, [api, filters, toast]);

  useEffect(() => { load(1); }, [load]);
  useEffect(() => {
    if (canFilterByUser) api('/users').then(setUsers).catch(() => {});
  }, [api, canFilterByUser]);

  const resetFilters = () => {
    setFilters({ user_id: '', action: '', from: '', to: '', search: '' });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0 }}>سجل العمليات {total > 0 && <span style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 400 }}>({total})</span>}</h2>
      </div>

      {!canFilterByUser && (
        <div style={{ background: 'var(--gray-50)', padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 13, color: 'var(--text-muted)' }}>
          تظهر هنا العمليات الخاصة بك فقط.
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="form-row" style={{ flexWrap: 'wrap', gap: 12 }}>
          {canFilterByUser && (
            <div className="form-group" style={{ flex: '1 1 180px' }}>
              <label className="form-label">المستخدم</label>
              <select className="form-select" value={filters.user_id} onChange={e => setFilters(f => ({ ...f, user_id: e.target.value }))}>
                <option value="">كل المستخدمين</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.name} — {ROLE_AR[u.role] || u.role}</option>
                ))}
              </select>
            </div>
          )}
          <div className="form-group" style={{ flex: '1 1 180px' }}>
            <label className="form-label">نوع العملية</label>
            <select className="form-select" value={filters.action} onChange={e => setFilters(f => ({ ...f, action: e.target.value }))}>
              <option value="">الكل</option>
              {Object.entries(ACTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ flex: '1 1 140px' }}>
            <label className="form-label">من تاريخ</label>
            <input type="date" className="form-input" value={filters.from} onChange={e => setFilters(f => ({ ...f, from: e.target.value }))} />
          </div>
          <div className="form-group" style={{ flex: '1 1 140px' }}>
            <label className="form-label">إلى تاريخ</label>
            <input type="date" className="form-input" value={filters.to} onChange={e => setFilters(f => ({ ...f, to: e.target.value }))} />
          </div>
          <div className="form-group" style={{ flex: '2 1 200px' }}>
            <label className="form-label">بحث</label>
            <input className="form-input" placeholder="بحث في الوصف..." value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} />
          </div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
            <button className="btn btn-outline btn-sm" onClick={resetFilters}>إعادة ضبط</button>
          </div>
        </div>
      </div>

      <div className="card">
        {loading ? <div className="loader">جاري التحميل...</div> : logs.length === 0 ? (
          <div className="empty"><div className="empty-icon">📜</div>لا توجد عمليات مسجلة</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>التاريخ</th>
                  <th>المستخدم</th>
                  <th>العملية</th>
                  <th>التفاصيل</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(l => (
                  <tr key={l.id}>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 12, color: 'var(--text-muted)' }}>{fmtDateTimeBoth(l.created_at)}</td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{l.user?.name || '—'}</div>
                      {l.user?.role && <div style={{ fontSize: 11, color: 'var(--text-faint)' }}>{ROLE_AR[l.user.role] || l.user.role}</div>}
                    </td>
                    <td>
                      <span className={`badge badge-${ACTION_BADGE[l.action] || 'pending'}`} style={{ fontSize: 11 }}>
                        {ACTION_LABELS[l.action] || l.action}
                      </span>
                    </td>
                    <td style={{ fontSize: 13 }}>{l.description || '—'}</td>
                    <td style={{ direction: 'ltr', textAlign: 'right', fontSize: 11, color: 'var(--text-faint)' }}>{l.ip_address || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {lastPage > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 16, alignItems: 'center' }}>
          <button className="btn btn-outline btn-sm" onClick={() => load(page - 1)} disabled={page <= 1}>السابق</button>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>صفحة {page} من {lastPage}</span>
          <button className="btn btn-outline btn-sm" onClick={() => load(page + 1)} disabled={page >= lastPage}>التالي</button>
        </div>
      )}
    </div>
  );
}
